<script setup lang="ts">
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { required, maxLength } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const router = useRouter();

const props = defineProps<{
	opportunityUuid: string;
}>();

const isDialogVisible = defineModel<boolean>();

const name = ref("");
const formRef = ref();

watch(isDialogVisible, (visible) => {
	if (visible) name.value = "";
});

const save = async () => {
	const { valid } = await formRef.value.validate();
	if (!valid) return;

	const response = await toastApiResult(
		useApiClient("campaignPlanner").createFlight(
			props.opportunityUuid,
			name.value
		)
	);

	if (response.isOk()) {
		router.push({
			name: "flight.detail",
			params: {
				flight_uuid: response.value.uuid,
			},
		});
	}
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" max-width="480" persistent>
		<v-card>
			<v-toolbar color="lightprimary" elevation="0" flat>
				<template #prepend>
					<v-icon class="ml-3">fa: fa-light fa-billboard</v-icon>
				</template>
				<v-toolbar-title>Add Flight</v-toolbar-title>
				<template #append>
					<v-btn icon variant="text" @click="isDialogVisible = false">
						<v-icon>fa: fa-regular fa-xmark</v-icon>
					</v-btn>
				</template>
			</v-toolbar>

			<v-divider />

			<v-form ref="formRef" @submit.prevent="save">
				<v-card-text class="pa-6">
					<div class="text-subtitle-1 font-weight-semibold mb-1">
						Flight name
					</div>
					<div class="text-body-2 text-medium-emphasis mb-2">
						Give this flight a descriptive name.
					</div>
					<v-text-field
						v-model="name"
						variant="filled"
						autofocus
						hide-details="auto"
						:rules="[required(t), maxLength(t, 255)]"
					/>
				</v-card-text>
			</v-form>

			<v-divider />

			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" @click="isDialogVisible = false">Cancel</v-btn>
				<v-btn variant="tonal" color="primary" @click="save">Create</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>
